export interface SAPCandidate {
  id: string;
  firstName: string;
  lastName: string;
  firstNameAr: string;
  lastNameAr: string;
  region: string;
  province: string;
  symbol: string; // Symbole choisi par le candidat indépendant (ex: 🏃‍♂️, 🕊️)
  photoUrl?: string;
  focusFr: string;
  focusAr: string;
  status: "valide" | "en_attente" | "rejete" | "brouillon";
  listType?: "locale" | "regionale";
  genre: "H" | "F";
  sociopro: string;
  age: number;
  whatsapp?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  programPdfUrl?: string;
  justificatifUrl?: string;
}

import { create } from 'zustand';
import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();

interface SapState {
  candidates: SAPCandidate[];
  isLoaded: boolean;
  fetchCandidates: () => Promise<void>;
  addCandidateInternal: (candidate: SAPCandidate) => void;
  deleteCandidateInternal: (id: string) => void;
  updateCandidateInternal: (id: string, updates: Partial<SAPCandidate>) => void;
}

export const useSapStore = create<SapState>((set, get) => ({
  candidates: [],
  isLoaded: false,

  fetchCandidates: async () => {
    if (get().isLoaded) return;
    try {
      const res = await fetch(`${API_URL}/sap.php`);
      const data = await res.json();
      if (Array.isArray(data)) {
        // Map database integer/strings to correct types if needed
        const parsedData = data.map(c => ({
          ...c,
          age: parseInt(c.age, 10) || 0
        }));
        set({ candidates: parsedData as SAPCandidate[], isLoaded: true });
      }
    } catch (err) {
      console.error("Error fetching SAP candidates:", err);
      set({ candidates: [], isLoaded: true });
    }
  },

  addCandidateInternal: (candidate) => {
    set((state) => {
      const exists = state.candidates.some(c => c.id === candidate.id);
      if (exists) {
        return { candidates: state.candidates.map(c => c.id === candidate.id ? candidate : c) };
      }
      return { candidates: [candidate, ...state.candidates] };
    });
    fetch(`${API_URL}/sap.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'add', candidate })
    });
  },

  deleteCandidateInternal: (id) => {
    set((state) => ({ candidates: state.candidates.filter(c => c.id !== id) }));
    fetch(`${API_URL}/sap.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id })
    });
  },

  updateCandidateInternal: (id, updates) => {
    set((state) => ({
      candidates: state.candidates.map(c => c.id === id ? { ...c, ...updates } : c)
    }));
    const updatedCandidate = get().candidates.find(c => c.id === id);
    if (updatedCandidate) {
      fetch(`${API_URL}/sap.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', candidate: updatedCandidate })
      });
    }
  }
}));

// Initial fetch if we are in browser
if (typeof window !== "undefined") {
  useSapStore.getState().fetchCandidates();
}

// Backwards compatibility wrappers
export function loadAllSAPCandidates(): SAPCandidate[] {
  const store = useSapStore.getState();
  if (!store.isLoaded) {
    store.fetchCandidates();
  }
  return store.candidates;
}

export function saveSAPCandidate(candidate: SAPCandidate) {
  useSapStore.getState().addCandidateInternal(candidate);
}

export function deleteSAPCandidate(id: string) {
  useSapStore.getState().deleteCandidateInternal(id);
}

export function getSAPCandidatesByRegion(regionName: string): SAPCandidate[] {
  return useSapStore.getState().candidates.filter(c => c.region === regionName && c.status === "valide");
}
